import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';

import MicrophoneImg from '../assets/images/Microphone.jpg';
import GuitarImg from '../assets/images/Guitar.jpg';
import PianoImg from '../assets/images/Piano.jpg';
import TablaImg from '../assets/images/Tabla_Set.jpeg';
import ViolinImg from '../assets/images/violin.jpg';
import SitarImg from '../assets/images/Sitar.jpeg';

const INSTRUMENTS = [
  { name: 'Microphone', type: 'mic', hindi: 'माइक्रोफोन', freq: 261.63, bg: '#100C06', accent: '#C8922A', desc: 'The modern vessel of sound. High-fidelity capture for vocals, instruments, and pristine room acoustics.', note: 'Sa', imageSrc: MicrophoneImg, imgPosition: 'center 30%' },
  { name: 'Guitar', type: 'guitar', hindi: 'गिटार', freq: 293.66, bg: '#1A1209', accent: '#D6A848', desc: 'Six strings of raw emotion. Versatile across genres, from rhythmic strumming to piercing solos.', note: 'Re', imageSrc: GuitarImg, imgPosition: 'center 20%' },
  { name: 'Piano', type: 'piano', hindi: 'पियानो', freq: 329.63, bg: '#100C06', accent: '#E8C161', desc: 'The backbone of harmony. 88 keys bridging melody and complex chordal structures perfectly.', note: 'Ga', imageSrc: PianoImg, imgPosition: 'center 40%' },
  { name: 'Tabla', type: 'tabla', hindi: 'तबला', freq: 349.23, bg: '#1A1209', accent: '#C8922A', desc: 'The heartbeat of Hindustani music. Intricate rhythmic cycles defined by distinct bols (syllables).', note: 'Ma', imageSrc: TablaImg, imgPosition: 'center 40%' },
  { name: 'Violin', type: 'violin', hindi: 'वायलिन', freq: 392.00, bg: '#100C06', accent: '#D6A848', desc: 'Bowed perfection. Capable of mimicking the human voice with unparalleled emotional resonance.', note: 'Pa', imageSrc: ViolinImg, imgPosition: 'center 30%' },
  { name: 'Sitar', type: 'sitar', hindi: 'सितार', freq: 523.25, bg: '#1A1209', accent: '#E8C161', desc: 'The iconic 18-stringed lute. Master meend (glides) and complex sympathetic resonances.', note: 'Sa', imageSrc: SitarImg, imgPosition: 'center 15%' },
];

function ImageCard({ inst, index, isLeft, rowRef, isMobile }) {
  const isInView = useInView(rowRef, { once: false, margin: "-25% 0px -25% 0px" });
  const cardRef = useRef(null);

  // Mouse-tracking state for hover parallax
  const [hoverOffset, setHoverOffset] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);



  const accentRGB = {
    r: parseInt(inst.accent.slice(1, 3), 16),
    g: parseInt(inst.accent.slice(3, 5), 16),
    b: parseInt(inst.accent.slice(5, 7), 16),
  };

  const handleMouseMove = useCallback((e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Normalise to -1..1 from card centre
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setHoverOffset({ x, y });
  }, []);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setHoverOffset({ x: 0, y: 0 });
  }, []);

  // Image shifts up to ±18px on hover following the cursor
  const imgHoverX = isHovered ? hoverOffset.x * 18 : 0;
  const imgHoverY = isHovered ? hoverOffset.y * 18 : 0;
  // Card itself tilts subtly — 3D tilt feel
  const rotateX = isHovered ? -hoverOffset.y * 4 : 0;
  const rotateY = isHovered ? hoverOffset.x * 4 : 0;

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, x: isLeft ? 100 : -100 }}
      animate={isInView
        ? { opacity: 1, x: 0 }
        : { opacity: 0, x: isLeft ? 100 : -100 }
      }
      transition={{ duration: 1.0, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
      style={{
        width: 'clamp(280px, 30vw, 400px)',
        height: 'clamp(420px, 58vh, 600px)',
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        cursor: 'none',
        boxShadow: `0 40px 80px -20px rgba(0,0,0,0.9), 0 0 0 1px rgba(${accentRGB.r},${accentRGB.g},${accentRGB.b},${isHovered ? 0.4 : 0.14})`,
        rotateX,
        rotateY,
        transformPerspective: 800,
        transition: 'box-shadow 0.3s ease',
      }}
    >
      {/* Dark base so full image has a clean backdrop */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: inst.bg,
      }} />

      {/* Subtle accent glow in corner */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: `radial-gradient(ellipse at 30% 20%, rgba(${accentRGB.r},${accentRGB.g},${accentRGB.b},0.07) 0%, transparent 65%)`,
        pointerEvents: 'none',
      }} />

      {/* Image — full image with contain, hover parallax applied */}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
        }}
        animate={{
          x: imgHoverX,
          y: imgHoverY,
          scale: isInView ? (isHovered ? 1.04 : 1.0) : 1.08,
        }}
        transition={{
          x: { type: 'spring', stiffness: 180, damping: 22 },
          y: { type: 'spring', stiffness: 180, damping: 22 },
          scale: isHovered
            ? { type: 'spring', stiffness: 200, damping: 25 }
            : { duration: 1.5, ease: [0.16, 1, 0.3, 1] },
        }}
      >
        <img
          src={inst.imageSrc}
          alt={inst.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center center',
            display: 'block',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
      </motion.div>

      {/* Studio key light overlay */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
        background: `
          radial-gradient(ellipse 65% 55% at 18% 8%, rgba(255,255,255,0.06) 0%, transparent 68%),
          linear-gradient(158deg, transparent 55%, rgba(0,0,0,0.35) 100%)
        `,
      }} />

      {/* Rim light — gold on top edge, brightens on hover */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none',
        borderRadius: 4,
        boxShadow: `inset 0 1px 0 rgba(${accentRGB.r},${accentRGB.g},${accentRGB.b},${isHovered ? 0.7 : 0.45})`,
        background: `linear-gradient(to bottom, rgba(${accentRGB.r},${accentRGB.g},${accentRGB.b},${isHovered ? 0.18 : 0.1}) 0%, transparent 25%)`,
        transition: 'all 0.3s ease',
      }} />

      {/* Bottom vignette — lighter so full image breathes */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none',
        background: `linear-gradient(to top, rgba(13,9,4,0.70) 0%, rgba(13,9,4,0.08) 30%, transparent 50%)`,
      }} />

      {/* Custom cursor dot that follows mouse inside card */}
      {isHovered && (
        <div style={{
          position: 'absolute',
          left: `calc(${(hoverOffset.x + 1) / 2 * 100}% )`,
          top: `calc(${(hoverOffset.y + 1) / 2 * 100}%)`,
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: inst.accent,
          boxShadow: `0 0 12px ${inst.accent}`,
          transform: 'translate(-50%, -50%)',
          zIndex: 8,
          pointerEvents: 'none',
          transition: 'left 0.05s linear, top 0.05s linear',
        }} />
      )}

      {/* Bottom label */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 6,
        padding: '28px 24px 24px',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ width: 24, height: 1, background: `linear-gradient(to right, ${inst.accent}, transparent)`, marginBottom: 8 }} />
          <span style={{ fontSize: 9, letterSpacing: '3.5px', color: inst.accent, textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif", fontWeight: 400, display: 'block' }}>
            {inst.name}
          </span>
        </div>
        <span style={{ fontSize: 13, fontFamily: "'Cormorant Garamond', serif", color: 'rgba(250,247,240,0.35)', fontStyle: 'italic', letterSpacing: '0.5px' }}>
          {inst.freq} Hz
        </span>
      </div>
    </motion.div>
  );
}

function InstrumentCard({ inst, index, rowRef, isMobile }) {
  const isInView = useInView(rowRef, { once: false, margin: "-25% 0px -25% 0px" });
  const isLeft = index % 2 === 0;

  return (
    <>
      {/* Huge Background Hindi Ghost Text */}
      <motion.div
        initial={{ opacity: 0, x: isMobile ? 0 : (isLeft ? -100 : 100) }}
        animate={isInView ? { opacity: isMobile ? 0.02 : 0.03, x: 0 } : { opacity: 0, x: isMobile ? 0 : (isLeft ? -100 : 100) }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'absolute',
          left: isMobile ? '50%' : (isLeft ? '-5%' : 'auto'),
          right: isMobile ? 'auto' : (!isLeft ? '-5%' : 'auto'),
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: isMobile ? 'clamp(140px, 40vw, 240px)' : 'clamp(180px, 28vw, 400px)',
          fontWeight: 700,
          color: inst.accent,
          whiteSpace: 'nowrap',
          zIndex: 0,
          pointerEvents: 'none',
          userSelect: 'none',
          top: isMobile ? '45%' : '50%',
          transform: isMobile ? 'translate(-50%, -50%)' : 'translateY(-50%)',
        }}
      >
        {inst.hindi}
      </motion.div>

      {/* Text Content Block */}
      <motion.div
        initial={{ opacity: 0, y: 50, filter: 'blur(10px)' }}
        animate={isInView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : { opacity: 0, y: 50, filter: 'blur(10px)' }}
        transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: 420,
          background: 'rgba(26, 18, 9, 0.25)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(250,247,240,0.03)',
          borderTopColor: `rgba(${parseInt(inst.accent.slice(1, 3), 16)}, ${parseInt(inst.accent.slice(3, 5), 16)}, ${parseInt(inst.accent.slice(5, 7), 16)}, 0.15)`,
          padding: '52px 44px',
          borderRadius: 2,
          boxShadow: `0 30px 60px -20px rgba(0,0,0,0.8)`,
          flexShrink: 0,
        }}
        whileHover={{
          borderColor: 'rgba(200,146,42,0.15)',
          background: 'rgba(26, 18, 9, 0.4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <span style={{ fontSize: 10, letterSpacing: '4px', color: inst.accent, textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif" }}>
            {String(index + 1).padStart(2, '0')} — {String(INSTRUMENTS.length).padStart(2, '0')}
          </span>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${inst.accent}44, transparent)` }} />
          <span style={{ fontSize: 14, fontFamily: "'Cormorant Garamond', serif", color: inst.accent, fontStyle: 'italic' }}>
            {inst.note} Note
          </span>
        </div>

        <h2 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(40px, 4.5vw, 60px)',
          fontWeight: 300,
          color: '#FAF7F0',
          margin: '0 0 24px 0',
          lineHeight: 1,
          letterSpacing: '-1px',
        }}>
          {inst.name}
        </h2>

        <p style={{
          fontSize: 15,
          color: 'rgba(250,247,240,0.5)',
          lineHeight: 1.8,
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 300,
          margin: 0,
        }}>
          {inst.desc}
        </p>
      </motion.div>
    </>
  );
}

function InstrumentRow({ inst, index, isMobile }) {
  const rowRef = useRef(null);
  const isLeft = index % 2 === 0;
  const isInView = useInView(rowRef, { once: false, margin: isMobile ? "-10% 0px -10% 0px" : "-25% 0px -25% 0px" });

  const accentRGB = {
    r: parseInt(inst.accent.slice(1, 3), 16),
    g: parseInt(inst.accent.slice(3, 5), 16),
    b: parseInt(inst.accent.slice(5, 7), 16),
  };

  return (
    <div
      ref={rowRef}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: isMobile ? 'auto' : '100vh',
        padding: isMobile ? '40px 6%' : '60px 6%',
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box',
        flexDirection: isMobile ? 'column' : (isLeft ? 'row' : 'row-reverse'),
        gap: isMobile ? '32px' : '2%',
      }}
    >
      {/* Text Card Side */}
      <div style={{
        display: 'flex',
        justifyContent: isMobile ? 'center' : (isLeft ? 'flex-start' : 'flex-end'),
        alignItems: 'center',
        width: isMobile ? '100%' : '38%',
        position: 'relative',
        flexShrink: 0,
        zIndex: 10,
      }}>
        <InstrumentCard inst={inst} index={index} rowRef={rowRef} isMobile={isMobile} />
      </div>

      {/* Central Spine Area — node + dual connectors */}
      <div style={{
        width: '24%',
        display: isMobile ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        flexShrink: 0,
        height: '100%',
      }}>
        {/* Left connector */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={isInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: '50%',
            height: 1,
            background: `linear-gradient(to right, transparent, rgba(${accentRGB.r},${accentRGB.g},${accentRGB.b},0.5))`,
            transformOrigin: 'left',
          }}
        />

        {/* Spine Node */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#0D0904',
            border: `2px solid ${inst.accent}`,
            boxShadow: `0 0 20px ${inst.accent}66`,
            zIndex: 5,
            flexShrink: 0,
          }}
        />

        {/* Right connector */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={isInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            right: 0,
            height: 1,
            background: `linear-gradient(to left, transparent, rgba(${accentRGB.r},${accentRGB.g},${accentRGB.b},0.5))`,
            transformOrigin: 'right',
          }}
        />
      </div>

      {/* Image Card Side */}
      <div style={{
        display: 'flex',
        justifyContent: isMobile ? 'center' : (isLeft ? 'flex-end' : 'flex-start'),
        alignItems: 'center',
        width: isMobile ? '100%' : '38%',
        flexShrink: 0,
      }}>
        <ImageCard inst={inst} index={index} isLeft={isLeft} rowRef={rowRef} isMobile={isMobile} />
      </div>
    </div>
  );
}

export default function InstrumentShowcase() {
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end end"]
  });

  const spineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const spineOpacity = useTransform(scrollYProgress, [0, 0.05, 0.95, 1], [0, 1, 1, 0]);

  return (
    <section
      ref={containerRef}
      id="instruments"
      style={{ background: '#0D0904', position: 'relative', padding: '160px 0 80px', overflowX: 'hidden' }}
    >
      {/* Introduction Title */}
      <div style={{ textAlign: 'center', marginBottom: '120px', position: 'relative', zIndex: 10 }}>
        <span style={{
          fontSize: 11,
          letterSpacing: '4px',
          textTransform: 'uppercase',
          color: '#C8922A',
          fontFamily: "'DM Sans', sans-serif",
          display: 'block',
          marginBottom: 16,
        }}>
          The Arsenal
        </span>
        <h2 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(48px, 6vw, 84px)',
          color: '#FAF7F0',
          fontWeight: 300,
          margin: 0,
          lineHeight: 1,
        }}>
          Instruments of Sound
        </h2>
      </div>

      {/* Content container with spine */}
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>

        {/* Central Glowing Spine */}
        <div style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          bottom: 0,
          width: 1,
          background: 'rgba(250,247,240,0.03)',
          transform: 'translateX(-50%)',
          zIndex: 1,
          pointerEvents: 'none',
          display: isMobile ? 'none' : 'block',
        }}>
          <motion.div
            style={{
              width: '100%',
              height: spineHeight,
              opacity: spineOpacity,
              background: 'linear-gradient(to bottom, transparent, #C8922A, #C8922A)',
              boxShadow: '0 4px 20px #C8922A',
            }}
          />
        </div>

        {/* Instrument rows */}
        {INSTRUMENTS.map((inst, i) => (
          <InstrumentRow key={inst.name} inst={inst} index={i} isMobile={isMobile} />
        ))}
      </div>
    </section>
  );
}
